import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { getAvailability, updateAvailability } from '../services/api';
import './AvailabilityModal.css';

// NER-1386 (M3): editor de horario semanal por profesional. Contrato NER-1475:
// dayOfWeek 0-6 (Dom=0, convención JS getDay()); startTime/endTime HH:mm 24h;
// PUT reemplaza TODO el set de reglas.
const weekOrder = (day) => (day === 0 ? 7 : day); // Lun..Dom para ordenar/mostrar

function AvailabilityModal({ professional, onClose }) {
  const { t } = useTranslation();
  const days = t('availability.days', { returnObjects: true });
  const dayLabels = Array.isArray(days) ? days : [];

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const keyRef = useRef(0);
  const nextKey = () => {
    keyRef.current += 1;
    return `r${keyRef.current}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAvailability(professional.professionalId);
      const rows = (res?.rules || [])
        .map((r) => ({
          key: nextKey(),
          dayOfWeek: r.dayOfWeek,
          startTime: r.startTime || '',
          endTime: r.endTime || '',
        }))
        .sort(
          (a, b) =>
            weekOrder(a.dayOfWeek) - weekOrder(b.dayOfWeek) ||
            a.startTime.localeCompare(b.startTime)
        );
      setRules(rows);
    } catch (err) {
      console.error('Error loading availability:', err);
      setError(t('availability.loadError'));
    } finally {
      setLoading(false);
    }
  }, [professional.professionalId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const addRow = () => {
    setRules((rs) => [
      ...rs,
      { key: nextKey(), dayOfWeek: 1, startTime: '09:00', endTime: '17:00' },
    ]);
  };

  const updateRow = (key, field, value) => {
    setRules((rs) =>
      rs.map((r) =>
        r.key === key ? { ...r, [field]: field === 'dayOfWeek' ? Number(value) : value } : r
      )
    );
  };

  const removeRow = (key) => {
    setRules((rs) => rs.filter((r) => r.key !== key));
  };

  const save = async () => {
    // Validación: cada franja necesita inicio y fin, y start < end.
    for (const r of rules) {
      if (!r.startTime || !r.endTime || r.startTime >= r.endTime) {
        setError(t('availability.invalid'));
        return;
      }
    }
    setSaving(true);
    setError('');
    const payload = rules.map((r) => ({
      dayOfWeek: r.dayOfWeek,
      startTime: r.startTime,
      endTime: r.endTime,
    }));
    try {
      await updateAvailability(professional.professionalId, payload);
      onClose();
    } catch (err) {
      console.error('Error saving availability:', err);
      setError(t('availability.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('availability.forProfessional', { name: professional.name })}</h2>
        </div>
        <div className="modal-body">
          <p className="availability-subtitle">{t('availability.subtitle')}</p>
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {rules.length === 0 ? (
                <div className="empty-state">{t('availability.empty')}</div>
              ) : (
                <div className="availability-rows">
                  {rules.map((r) => (
                    <div className="availability-row" key={r.key}>
                      <select
                        className="availability-day"
                        value={r.dayOfWeek}
                        onChange={(e) => updateRow(r.key, 'dayOfWeek', e.target.value)}
                        aria-label={t('availability.day')}
                      >
                        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                          <option key={d} value={d}>
                            {dayLabels[d] || d}
                          </option>
                        ))}
                      </select>
                      <input
                        type="time"
                        value={r.startTime}
                        onChange={(e) => updateRow(r.key, 'startTime', e.target.value)}
                        aria-label={t('availability.start')}
                      />
                      <span className="availability-sep">–</span>
                      <input
                        type="time"
                        value={r.endTime}
                        onChange={(e) => updateRow(r.key, 'endTime', e.target.value)}
                        aria-label={t('availability.end')}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeRow(r.key)}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button type="button" className="btn btn-ghost btn-sm" onClick={addRow}>
                <Plus size={15} />
                {t('availability.addRange')}
              </button>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={saving || loading}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AvailabilityModal;
